// The pricing engine: who sees which price for which module. Pure functions
// shared by the edge functions (monetization-quote, create-checkout) and the
// browser (src/lib/monetization.ts). Plan: HANDOFF.md §9.14.
//
// Rules that hold everywhere:
//   - One person, one price: /pricing, every offer and checkout call
//     resolvePrice; an experiment arm holds for its whole window.
//   - Prices come only from PRICE_LADDER (the database enforces it too).
//   - The tier comes from the phone's country, else the visitor's country as
//     seen by our own edge (/__ctx, signed); never from the card at checkout.
import { DIAL_TERRITORIES } from "./dialCountries.ts";

export type Tier = "A" | "B" | "C";
export type UserType = "renter" | "owner" | "property_manager";
export type Network = "hosting" | "relay" | "mobile" | "other" | "unknown";

/** Every price we may charge, in USD cents. Mirrors public.price_ladder. */
export const PRICE_LADDER = [0, 5, 9, 15, 19, 25, 29, 39, 49, 59, 69, 79, 99, 129, 149, 199, 249, 299, 399, 499].map((d) => d * 100);
export const isLadderPrice = (cents: number, ladder: number[] = PRICE_LADDER) => ladder.includes(cents);
/** A point an admin may add to the ladder: whole dollars ending in 5 or 9. */
export const isStandardPrice = (cents: number) => cents === 0 || (cents > 0 && cents % 100 === 0 && [5, 9].includes((cents / 100) % 10));

export const formatUsd = (cents: number) => (cents === 0 ? "Free" : `US$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);

// ── Countries and tiers ───────────────────────────────────────────────────
// Calling codes, longest first, each with its territories (main country first).
const BY_CODE = new Map<string, { iso: string; matcher: RegExp | null }[]>();
for (const [iso, code, leading] of DIAL_TERRITORIES) {
  BY_CODE.set(code, [...(BY_CODE.get(code) ?? []), { iso, matcher: leading ? new RegExp(`^(?:${leading})$`) : null }]);
}
const CODES_LONGEST_FIRST = [...BY_CODE.keys()].sort((a, b) => b.length - a.length);
// Mobile ranges shared with a territory belong to the main country (+61 4…
// Australia, not Cocos Islands; +212 6/7… Morocco, not Western Sahara).
// Mirrors src/lib/countryDialCodes.ts#MAIN_COUNTRY_SHARED_MOBILE.
export const MAIN_COUNTRY_SHARED_MOBILE: Readonly<Record<string, RegExp>> = { "61": /^4\d{8}$/, "212": /^[67]\d{8}$/ };

/**
 * "+46 701234567" → "SE"; null when the number has no calling code we know.
 * Codes shared by several countries (+1, +7, +44, +262, +590 …) are told
 * apart by the national number, the same way the app's phone picker does
 * (src/lib/countryDialCodes.ts#parsePhone): "+1 809…" is the Dominican
 * Republic, "+1 416…" Canada, not the US.
 */
export function phoneCountry(phone: string | null | undefined): string | null {
  const trimmed = (phone ?? "").trim();
  const compact = trimmed.replace(/[\s()-]/g, "");
  const digits = compact.startsWith("+") ? compact.slice(1).replace(/\D/g, "") : /^00\d/.test(compact) ? compact.slice(2).replace(/\D/g, "") : null;
  if (!digits) return null;
  for (const code of CODES_LONGEST_FIRST) {
    if (!digits.startsWith(code)) continue;
    const national = digits.slice(code.length);
    const all = BY_CODE.get(code)!;
    if (MAIN_COUNTRY_SHARED_MOBILE[code]?.test(national)) return all[0].iso;
    return (all.find((t) => t.matcher?.test(national)) ?? all[0]).iso;
  }
  return null;
}

/** Countries not listed are tier A. */
export const tierFor = (country: string | null | undefined, tiers: Record<string, Tier>): Tier =>
  (country && tiers[country.toUpperCase()]) || "A";

export interface PricingRow {
  tier: Tier;
  tier_source: "phone" | "ip" | "default";
  origin_country: string | null;
  relock_tier: Tier | null;
  relock_at: string | null;
  relocked_at: string | null;
  saw_logged_out_price: boolean;
}

export interface TierInput {
  existing: PricingRow | null;
  phoneCountry: string | null;
  /** Only from a verified /__ctx signature; null otherwise. */
  ipCountry: string | null;
  network: Network;
  marketCountry: string;
  hasPurchased: boolean;
  /** When the person's current price window ends (relocks wait for it). */
  windowEndsAt: Date;
  tiers: Record<string, Tier>;
  now: Date;
}

export interface TierDecision {
  tier: Tier;
  /** What to write to user_pricing. */
  write: Partial<PricingRow> | null;
  /** True when this person must see the control price (unknown origin on a datacenter/VPN). */
  controlOnly: boolean;
}

/**
 * The tier lock, applied once:
 *  - lock at the first quote: the phone's country, else the edge country;
 *    a phone from the market's own country never beats a foreign edge country
 *    (a Swede living in Santa Teresa has a Costa Rican SIM);
 *  - a lock that came from the edge country is corrected once by the phone,
 *    only before any purchase, and only from the next price window;
 *  - a datacenter/VPN with no phone gets the control at tier A and no lock.
 */
export function resolveTier(i: TierInput): TierDecision {
  const t = (c: string | null) => tierFor(c, i.tiers);
  const foreignPhone = i.phoneCountry && i.phoneCountry !== i.marketCountry ? i.phoneCountry : null;
  const ip = i.network === "hosting" ? null : i.ipCountry;

  if (i.existing) {
    const e = i.existing;
    if (e.relock_tier && e.relock_at && new Date(e.relock_at) <= i.now) {
      return {
        tier: e.relock_tier,
        write: { tier: e.relock_tier, tier_source: "phone", origin_country: i.phoneCountry ?? e.origin_country, relock_tier: null, relock_at: null, relocked_at: i.now.toISOString() },
        controlOnly: false,
      };
    }
    if (e.tier_source === "ip" && !e.relocked_at && !e.relock_tier && !i.hasPurchased && foreignPhone && t(foreignPhone) !== e.tier) {
      return { tier: e.tier, write: { relock_tier: t(foreignPhone), relock_at: i.windowEndsAt.toISOString() }, controlOnly: false };
    }
    return { tier: e.tier, write: null, controlOnly: false };
  }

  const fromPhone = i.phoneCountry && (i.phoneCountry !== i.marketCountry || !ip || ip === i.marketCountry);
  if (fromPhone) {
    return { tier: t(i.phoneCountry), write: { tier: t(i.phoneCountry), tier_source: "phone", origin_country: i.phoneCountry }, controlOnly: false };
  }
  if (ip) return { tier: t(ip), write: { tier: t(ip), tier_source: "ip", origin_country: ip }, controlOnly: false };
  return { tier: "A", write: null, controlOnly: i.network === "hosting" };
}

// ── The price book ────────────────────────────────────────────────────────
export interface PricePoint {
  id: string;
  module_key: string;
  tenant_id: string | null;
  user_type: UserType | null;
  tier: Tier | null;
  amount_usd_cents: number;
  active: boolean;
  in_book: boolean;
}

/** The most specific active book row (market 4, user type 2, tier 1). A
 *  missing tier row falls back to the all-tier row, which is the tier A price. */
export function bookPrice(points: PricePoint[], module: string, tenant: string, userType: UserType, tier: Tier): PricePoint | null {
  const score = (p: PricePoint) => (p.tenant_id ? 4 : 0) + (p.user_type ? 2 : 0) + (p.tier ? 1 : 0);
  return points
    .filter((p) => p.active && p.in_book && p.module_key === module
      && (p.tenant_id === null || p.tenant_id === tenant)
      && (p.user_type === null || p.user_type === userType)
      && (p.tier === null || p.tier === tier))
    .sort((a, b) => score(b) - score(a))[0] ?? null;
}

// ── Experiments ───────────────────────────────────────────────────────────
export interface Experiment {
  id: string;
  key: string;
  module_key: string;
  status: string;
  tenants: string[] | null;
  user_types: string[] | null;
  tiers: string[] | null;
  randomize_by: "user" | "market_period";
  period_days: number;
  holdout_pct: number;
  window_days: number;
  started_at: string | null;
}
export interface Variant { experiment_id: string; key: string; weight: number; price_point_id: string | null; is_control: boolean; factors?: Record<string, unknown> }
export interface Assignment { experiment_id: string; unit_id: string; variant_key: string; expires_at: string; ended_by_purchase_at: string | null }

export const experimentApplies = (e: Experiment, module: string, tenant: string, userType: UserType, tier: Tier) =>
  e.status === "running" && e.module_key === module
  && (!e.tenants?.length || e.tenants.includes(tenant))
  && (!e.user_types?.length || e.user_types.includes(userType))
  && (!e.tiers?.length || e.tiers.includes(tier));

/** A stable number in [0, 1) from a string (SHA-256). */
export async function bucket(seed: string): Promise<number> {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(seed)));
  return (((h[0] << 24) >>> 0) + (h[1] << 16) + (h[2] << 8) + h[3]) / 2 ** 32;
}

/** What the arm is drawn from: the person, or the market's current period
 *  (so owners in one town who compare notes see the same price). */
export function randomizationUnit(e: Experiment, profileKey: string, tenant: string, now: Date): string {
  if (e.randomize_by === "user") return profileKey;
  const start = e.started_at ? new Date(e.started_at).getTime() : 0;
  const period = Math.max(0, Math.floor((now.getTime() - start) / (e.period_days * 86_400_000)));
  return `${tenant}:p${period}`;
}

export async function drawVariant(e: Experiment, variants: Variant[], unit: string): Promise<Variant | null> {
  const arms = variants.filter((v) => v.experiment_id === e.id);
  if (!arms.length) return null;
  const control = arms.find((v) => v.is_control) ?? null;
  if (e.holdout_pct > 0 && (await bucket(`${e.key}:holdout:${unit}`)) < e.holdout_pct / 100) return control;
  const total = arms.reduce((s, v) => s + v.weight, 0);
  let x = (await bucket(`${e.key}:arm:${unit}`)) * total;
  for (const v of arms) { if ((x -= v.weight) < 0) return v; }
  return arms[arms.length - 1];
}

export interface PriceContext {
  module: string;
  tenant: string;
  userType: UserType;
  tier: Tier;
  /** "u:<user id>" or "v:<visitor id>"; null for a visitor without analytics consent. */
  profileKey: string | null;
  /** Control only: no consent, saw a logged-out price before consenting, or a VPN. */
  controlOnly: boolean;
  now: Date;
}

export interface PriceDecision {
  amountCents: number;
  pricePointId: string;
  experimentId: string | null;
  variantKey: string | null;
  /** A new or renewed assignment to store (experiment_assignments). */
  assign: { experiment_id: string; unit_id: string; variant_key: string; expires_at: string; context: Record<string, unknown> } | null;
}

/**
 * The one price this person sees for this module now, or null when the
 * module has no price for them (then no offer is shown).
 */
export async function resolvePrice(
  ctx: PriceContext,
  data: { points: PricePoint[]; experiments: Experiment[]; variants: Variant[]; assignments: Assignment[] },
): Promise<PriceDecision | null> {
  const book = bookPrice(data.points, ctx.module, ctx.tenant, ctx.userType, ctx.tier);
  if (!book) return null;
  const control: PriceDecision = { amountCents: book.amount_usd_cents, pricePointId: book.id, experimentId: null, variantKey: null, assign: null };
  const exp = data.experiments.find((e) => experimentApplies(e, ctx.module, ctx.tenant, ctx.userType, ctx.tier));
  if (!exp || !ctx.profileKey || ctx.controlOnly) return control;

  const arms = data.variants.filter((v) => v.experiment_id === exp.id);
  const armPrice = (v: Variant | null | undefined): PriceDecision | null => {
    if (!v) return null;
    if (v.is_control || !v.price_point_id) return { ...control, experimentId: exp.id, variantKey: v.key };
    const p = data.points.find((pp) => pp.id === v.price_point_id && isLadderPrice(pp.amount_usd_cents));
    return p ? { amountCents: p.amount_usd_cents, pricePointId: p.id, experimentId: exp.id, variantKey: v.key, assign: null } : null;
  };

  // The person keeps their arm for the whole window, even if the market period rolls over.
  const held = data.assignments.find((a) => a.experiment_id === exp.id && a.unit_id === ctx.profileKey
    && !a.ended_by_purchase_at && new Date(a.expires_at) > ctx.now);
  if (held) return armPrice(arms.find((v) => v.key === held.variant_key)) ?? control;

  const unit = randomizationUnit(exp, ctx.profileKey, ctx.tenant, ctx.now);
  const v = await drawVariant(exp, arms, unit);
  const priced = armPrice(v);
  if (!v || !priced) return control;
  return {
    ...priced,
    assign: {
      experiment_id: exp.id,
      unit_id: ctx.profileKey,
      variant_key: v.key,
      expires_at: new Date(ctx.now.getTime() + exp.window_days * 86_400_000).toISOString(),
      context: { tenant: ctx.tenant, userType: ctx.userType, tier: ctx.tier, unit },
    },
  };
}

// ── Who is this? ──────────────────────────────────────────────────────────
/** Which of the person's roles a module is sold to. */
export function userTypeFor(audience: string[], profileTypes: string[] | null | undefined, declared?: string | null): UserType | null {
  const ok = (t: string | null | undefined): t is UserType => !!t && audience.includes(t);
  // "property_manager" is a legacy profile value (signup never offers it):
  // it buys as an owner.
  const asOwner = (t: string | null | undefined) => (t === "property_manager" ? "owner" : t);
  if (ok(asOwner(declared))) return asOwner(declared) as UserType;
  const types = (profileTypes ?? []).map(asOwner);
  for (const t of ["owner", "renter"] as const) if (types.includes(t) && ok(t)) return t;
  return null;
}

export type SourceGroup = "paid" | "social" | "organic" | "referral" | "direct";
const SOCIAL = /(^|\.)(facebook|instagram|t\.co|twitter|x|linkedin|tiktok|youtube|pinterest|reddit|whatsapp|wa\.me|threads)\./;
const SEARCH = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave|baidu|yandex)\./;

export function sourceGroup(referrerHost: string | null, utm: Record<string, string> | null, ownHost?: string): SourceGroup {
  const medium = (utm?.utm_medium ?? "").toLowerCase();
  if (/cpc|ppc|paid|display|ads?$/.test(medium) || utm?.gclid || utm?.fbclid) return "paid";
  if (/social/.test(medium)) return "social";
  const host = (referrerHost ?? "").toLowerCase();
  if (!host || (ownHost && host.endsWith(ownHost.replace(/^www\./, "")))) return medium ? "referral" : "direct";
  if (SOCIAL.test(`.${host}.`) || SOCIAL.test(`${host}.`)) return "social";
  if (SEARCH.test(`.${host}.`) || SEARCH.test(`${host}.`)) return "organic";
  return "referral";
}

export type BudgetBand = "under_1500" | "1500_3000" | "over_3000" | null;
export const budgetBand = (maxPriceUsd: number | null | undefined): BudgetBand =>
  maxPriceUsd == null || !Number.isFinite(maxPriceUsd) || maxPriceUsd <= 0 ? null
    : maxPriceUsd < 1500 ? "under_1500" : maxPriceUsd <= 3000 ? "1500_3000" : "over_3000";

export interface Audience {
  market: string;
  userType: UserType | null;
  tier: Tier;
  inMarketCountry: boolean | null;
  sourceGroup: SourceGroup;
  budgetBand: BudgetBand;
}

export function resolveAudience(i: {
  market: string; marketCountry: string; userType: UserType | null; tier: Tier; visitorCountry: string | null;
  referrerHost: string | null; utm: Record<string, string> | null; ownHost?: string; maxPriceUsd?: number | null;
}): Audience {
  return {
    market: i.market,
    userType: i.userType,
    tier: i.tier,
    inMarketCountry: i.visitorCountry ? i.visitorCountry === i.marketCountry : null,
    sourceGroup: sourceGroup(i.referrerHost, i.utm, i.ownHost),
    budgetBand: budgetBand(i.maxPriceUsd),
  };
}

// ── Signed tokens (the quote a person saw; the edge's view of the visitor) ──
const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));

async function hmac(secret: string, msg: string): Promise<string> {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return b64url(new Uint8Array(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(msg))));
}
const sameString = (a: string, b: string) => {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return d === 0;
};

export interface Quote {
  module: string;
  tenant: string;
  subject: string | null;
  userType: UserType;
  tier: Tier;
  amountCents: number;
  pricePointId: string;
  experimentId: string | null;
  variantKey: string | null;
  profileKey: string | null;
  /** Expiry, epoch ms. */
  exp: number;
}

export const QUOTE_TTL_MS = 30 * 60_000;

export async function signQuote(q: Quote, secret: string): Promise<string> {
  const body = b64url(new TextEncoder().encode(JSON.stringify(q)));
  return `${body}.${await hmac(secret, body)}`;
}

export async function verifyQuote(token: string, secret: string, now: number = Date.now()): Promise<Quote | null> {
  const [body, sig] = (token ?? "").split(".");
  if (!body || !sig || !sameString(sig, await hmac(secret, body))) return null;
  try {
    const q = JSON.parse(new TextDecoder().decode(fromB64url(body))) as Quote;
    return q.exp > now && isLadderPrice(q.amountCents) ? q : null;
  } catch { return null; }
}

/** The edge's signature over what it saw (worker/index.js signs the same message). */
export const ctxMessage = (c: { country: string | null; network: string; issuedAt: number }) => `${c.country ?? ""}|${c.network}|${c.issuedAt}`;

/** The visitor's country and network, trusted only when our edge signed them recently. */
export async function verifiedEdgeContext(
  c: { country?: string | null; network?: string; issuedAt?: number; sig?: string } | null | undefined,
  secret: string | undefined,
  now: number = Date.now(),
): Promise<{ country: string | null; network: Network }> {
  const network = (c?.network ?? "unknown") as Network;
  if (!c || !secret || !c.sig || typeof c.issuedAt !== "number" || now - c.issuedAt > 86_400_000 || c.issuedAt > now + 60_000) {
    return { country: null, network: network === "hosting" ? "hosting" : "unknown" };
  }
  const ok = sameString(c.sig, await hmac(secret, ctxMessage({ country: c.country ?? null, network, issuedAt: c.issuedAt })));
  return ok ? { country: c.country ?? null, network } : { country: null, network: "unknown" };
}

// ── Identity checks are paid for first ─────────────────────────────────────
// A Stripe Identity check costs us USD 1.50 a try (Felipe, 2026-10-13): no
// check starts before the person has paid, and no identity check is ever
// offered free or below the cost of the tries one purchase allows (3).
export const IDENTITY_TRY_CENTS = 150
export const MIN_VERIFICATION_CENTS = 500

/** Whether a price may be offered for this module (identity checks have a floor). */
export const verificationPriceOk = (module: string, cents: number): boolean =>
  !module.startsWith('verified_') || (Number.isFinite(cents) && cents >= MIN_VERIFICATION_CENTS)

/** Whether a Stripe Identity session may be created for a verification: only after its checkout was paid. */
export const canStartIdentityCheck = (checkout: { status?: string | null; module_key?: string | null; amount_usd_cents?: number | null } | null | undefined): boolean =>
  !!checkout && checkout.status === 'paid' && !!checkout.module_key?.startsWith('verified_')
  && verificationPriceOk(checkout.module_key, Number(checkout.amount_usd_cents))

